<script setup lang="ts">
    import { reactive, watch, onMounted, inject } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { SubscriptionAccess } from '@views/BackOffice/Subscription/Subscription.access';
    import Modal from '@shared/components/Modal.vue';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps, isEmpty } from '@shared/components/Form/Form';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);

    const props = defineProps<{
        show: boolean;
    }>();

    const emit = defineEmits<{
        close: [];
    }>();

    const state = reactive<{
        cancellationReasons: FormProps;
    }>({
        cancellationReasons: {
            data: {},
        },
    });

    const cancellationReasons = [
        'No longer have a need for the service (Value)',
        "It didn't meet my needs (Value)",
        'Have found another service (Competition)',
        'Feature set was less than expected (Customer Service & Experience)',
        'Quality was less than expected (Customer Service & Experience)',
        'Ease of use was less than expected (Customer Service & Experience)',
        'Customer service was less than expected (Customer Service & Experience)',
        'Abuse (Internal)',
        'Mistakenly created (Internal)',
        'Too expensive (Price)',
    ];

    function setupCancellationReasons(): void {
        state.cancellationReasons.data = {
            ReasonOptions: {
                modelValue: undefined,
                radioGroup: {
                    legend: 'Choose a reason',
                    options: cancellationReasons.map((reason) => {
                        return { value: reason, label: reason };
                    }),
                },
            },
            Reason: {
                modelValue: '',
                required: true,
                textArea: {
                    label: 'Comment',
                },
            },
        };
    }

    async function saveCancellationReasons() {
        state.cancellationReasons.setInitData = false;

        try {
            await subscriptionAccess.saveCancellationReasons(state.cancellationReasons.data.Reason.modelValue);
            state.cancellationReasons.setInitData = true;
            emit('close');
        } catch (err: any) {
            bsAlert({ type: AlertTypes.Error, content: `Could not save cancellation reasons: ${err?.message ?? err}` });
        }
    }

    function closeModal() {
        emit('close');
    }

    onMounted(() => {
        setupCancellationReasons();

        watch(
            () => state.cancellationReasons.data.ReasonOptions?.modelValue,
            (newVal) => {
                if (state.cancellationReasons.data.Reason) {
                    state.cancellationReasons.data.Reason.modelValue = newVal;
                }
            }
        );
    });
</script>

<template>
    <Modal :title="'Reason for cancellation'" :show="props.show" @close="closeModal">
        <template #content>
            <Form
                v-if="!isEmpty(state.cancellationReasons.data)"
                :data="state.cancellationReasons.data"
                :submitting="subscriptionAccess.state.savingCancellationReasons"
                :setInitData="state.cancellationReasons.setInitData"
                @update:isValid="(key, newVal) => (state.cancellationReasons!.data[key].isValid = newVal)"
                @update:modelValue="(key, newVal) => (state.cancellationReasons.data[key].modelValue = newVal)"
                @onSubmit="saveCancellationReasons"></Form>
        </template>
    </Modal>
</template>
